import { Copy, ExternalLink, Pencil, Tag, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { deleteBookmark, unassignBookmarkFromProject } from '@/api/bookmarks';
import { ConfirmDialog } from '@/components/Modals/ConfirmDialog';
import { EditBookmarkModal } from '@/components/Modals/EditBookmarkModal';
import { ManageTagsModal } from '@/components/Modals/ManageTagsModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSelection } from '@/hooks/useSelection';
import { absoluteTime, relativeTime, truncateUrl } from '@/lib/format';
import type { Bookmark } from '@/types/models';

/** Single-id (legacy) — used when an unselected row is dragged on its own. */
const DRAG_SINGLE_MIME = 'application/x-hb-bookmark-id';
/** Multi-id (new) — used when a selected row is dragged; carries the whole selection. */
const DRAG_MULTI_MIME = 'application/x-hb-bookmark-ids';

type TabContext = 'all' | 'project';

interface BookmarksTableProps {
  bookmarks: Bookmark[];
  onChanged: () => void;
  /**
   * Identifies the table's view context. Selection clears whenever this string
   * changes — callers compose it to include filters / project id so a context
   * switch doesn't carry a stale selection across.
   */
  scopeKey: string;
  tabContext: TabContext;
  /** When `tabContext === 'project'`, the id of the project being viewed. */
  projectId?: string;
}

export function BookmarksTable({
  bookmarks,
  onChanged,
  scopeKey,
  tabContext,
  projectId,
}: BookmarksTableProps) {
  const visibleIds = useMemo(() => bookmarks.map((b) => b.id), [bookmarks]);
  const selection = useSelection(visibleIds);

  const clearSelection = selection.clear;
  // Reset selection whenever the table's view context changes (tab switch,
  // project change, filter change). useSelection itself only intersects with
  // visibleIds — this is the explicit "fresh slate" trigger.
  useEffect(() => {
    clearSelection();
  }, [scopeKey, clearSelection]);

  const [editing, setEditing] = useState<Bookmark | null>(null);
  const [deleting, setDeleting] = useState<Bookmark | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkUnassignOpen, setBulkUnassignOpen] = useState(false);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);

  const selectedBookmarks = useMemo(
    () => bookmarks.filter((b) => selection.isSelected(b.id)),
    [bookmarks, selection],
  );

  if (bookmarks.length === 0) {
    return (
      <div className="text-muted-foreground text-sm py-12 text-center">
        No bookmarks match the current filters.
      </div>
    );
  }

  return (
    <TooltipProvider>
      {selection.count > 0 && (
        <BulkActionBar
          count={selection.count}
          onClear={selection.clear}
          onDelete={() => setBulkDeleteOpen(true)}
          onManageTags={() => setManageTagsOpen(true)}
          onUnassign={
            tabContext === 'project' && projectId ? () => setBulkUnassignOpen(true) : undefined
          }
        />
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-9">
              <Checkbox
                checked={
                  selection.isAllSelected ? true : selection.isPartial ? 'indeterminate' : false
                }
                onCheckedChange={() => selection.toggleAll()}
                aria-label="Select all visible bookmarks"
              />
            </TableHead>
            <TableHead>Title</TableHead>
            <TableHead>URL</TableHead>
            <TableHead>Tags</TableHead>
            <TableHead>Projects</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookmarks.map((b) => {
            const isSelected = selection.isSelected(b.id);
            const dragMode =
              isSelected && selection.count > 1 ? 'multi' : ('single' as const);
            return (
              <TableRow
                key={b.id}
                draggable
                onDragStart={(e) => {
                  if (dragMode === 'multi') {
                    const ids = Array.from(selection.selectedIds);
                    e.dataTransfer.setData(DRAG_MULTI_MIME, JSON.stringify(ids));
                  } else {
                    e.dataTransfer.setData(DRAG_SINGLE_MIME, b.id);
                  }
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                data-state={isSelected ? 'selected' : undefined}
                className="cursor-grab active:cursor-grabbing"
              >
                <TableCell className="w-9">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => selection.toggle(b.id)}
                    aria-label={`Select ${b.title}`}
                  />
                </TableCell>
                <TableCell className="font-medium">
                  <a
                    href={b.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {b.title}
                  </a>
                </TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-2 text-muted-foreground">
                        {truncateUrl(b.url, 50)}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-5 w-5"
                          aria-label="Copy URL"
                          onClick={() => {
                            void navigator.clipboard.writeText(b.url);
                            toast.success('URL copied');
                          }}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{b.url}</TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {b.tags.map((t) => (
                      <Badge key={t} variant="outline">
                        {t}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {b.projectIds.length === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <Badge variant="secondary">{b.projectIds.length} projects</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-muted-foreground">{relativeTime(b.createdAt)}</span>
                    </TooltipTrigger>
                    <TooltipContent>{absoluteTime(b.createdAt)}</TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Open"
                      onClick={() => window.open(b.url, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Edit"
                      onClick={() => setEditing(b)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Delete"
                      onClick={() => setDeleting(b)}
                    >
                      <Trash2 />
                    </Button>
                    {tabContext === 'project' && projectId && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            await unassignBookmarkFromProject(b.id, projectId);
                            toast.success('Unassigned');
                            onChanged();
                          } catch (err) {
                            toast.error((err as Error).message);
                          }
                        }}
                      >
                        Unassign
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <EditBookmarkModal bookmark={editing} onClose={() => setEditing(null)} onSaved={onChanged} />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete bookmark?"
        description={deleting ? `Permanently delete "${deleting.title}".` : undefined}
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await deleteBookmark(deleting.id);
            toast.success('Bookmark deleted');
            onChanged();
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setDeleting(null);
          }
        }}
        onCancel={() => setDeleting(null)}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selection.count} bookmark${selection.count === 1 ? '' : 's'}?`}
        description="Permanently delete the selected bookmarks. This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          setBulkDeleteOpen(false);
          let succeeded = 0;
          let failed = 0;
          for (const b of selectedBookmarks) {
            try {
              await deleteBookmark(b.id);
              succeeded += 1;
            } catch {
              failed += 1;
            }
          }
          if (failed === 0) {
            toast.success(`Deleted ${succeeded} bookmark${succeeded === 1 ? '' : 's'}`);
          } else {
            toast.error(`${succeeded} deleted, ${failed} failed`);
          }
          onChanged();
        }}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      <ConfirmDialog
        open={bulkUnassignOpen}
        title={`Unassign ${selection.count} bookmark${selection.count === 1 ? '' : 's'} from this project?`}
        description="The bookmarks themselves are not deleted; only the assignment to this project is removed."
        confirmLabel="Unassign"
        destructive
        onConfirm={async () => {
          if (!projectId) return;
          setBulkUnassignOpen(false);
          let succeeded = 0;
          let failed = 0;
          for (const b of selectedBookmarks) {
            try {
              await unassignBookmarkFromProject(b.id, projectId);
              succeeded += 1;
            } catch {
              failed += 1;
            }
          }
          if (failed === 0) {
            toast.success(`Unassigned ${succeeded} bookmark${succeeded === 1 ? '' : 's'}`);
          } else {
            toast.error(`${succeeded} unassigned, ${failed} failed`);
          }
          onChanged();
        }}
        onCancel={() => setBulkUnassignOpen(false)}
      />

      <ManageTagsModal
        open={manageTagsOpen}
        onClose={() => setManageTagsOpen(false)}
        bookmarks={selectedBookmarks}
        onUpdated={onChanged}
      />
    </TooltipProvider>
  );
}

interface BulkActionBarProps {
  count: number;
  onClear: () => void;
  onDelete: () => void;
  onManageTags: () => void;
  /** Only provided in Project Bookmarks tab context. */
  onUnassign?: () => void;
}

function BulkActionBar({ count, onClear, onDelete, onManageTags, onUnassign }: BulkActionBarProps) {
  return (
    <div className="sticky top-0 z-10 mb-2 flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm">
      <span className="font-medium">{count} selected</span>
      <span className="text-muted-foreground hidden sm:inline">
        · drag any selected row onto a project to assign all
      </span>
      <div className="ml-auto flex items-center gap-1">
        <Button size="sm" variant="ghost" onClick={onManageTags}>
          <Tag />
          Manage tags…
        </Button>
        {onUnassign && (
          <Button size="sm" variant="outline" onClick={onUnassign}>
            Unassign from this project
          </Button>
        )}
        <Button size="sm" variant="destructive" onClick={onDelete}>
          <Trash2 />
          Delete
        </Button>
        <Button size="sm" variant="ghost" onClick={onClear} aria-label="Clear selection">
          <X />
        </Button>
      </div>
    </div>
  );
}
